/**
 * COMMERCE-176C-R3 — persist legal consent to Worker before unlocking shop.
 * CUSTOMER-LEGAL-P0 — wait for initData; fail closed on non-JSON Pages proxy responses.
 * COMM-R1.1 — optional marketing_opt_in is a separate evidence write; legal success unlocks store.
 */
import { waitForTelegramInitData } from "../lib/telegram.js";
import { CONSENT_VERSION } from "./store-info-v2.js";
import { storeV2AuthHeaders } from "./store-v2-auth-headers-v2.js";
import { telegramUserId } from "./customer-profile-v2.js";

async function readJsonBody(res: Response): Promise<Record<string, unknown> | null> {
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) return null;
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export async function persistLegalConsentRemote(input: {
  sessionKey: string;
  locale: string;
  buildId?: string | null;
  /** Explicit optional marketing GRANTED. Default false — unchecked must not invent grant. */
  marketingOptIn?: boolean;
}): Promise<{
  ok: boolean;
  duplicate?: boolean;
  error?: string;
  marketing?: { attempted: boolean; ok: boolean; granted: boolean; error?: string };
}> {
  try {
    await waitForTelegramInitData(2500);
    const res = await fetch("/api/store-v2/legal-consent", {
      method: "POST",
      headers: storeV2AuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({
        session_key: input.sessionKey,
        locale: input.locale,
        customer_app_build: input.buildId ?? CONSENT_VERSION,
        telegram_user_id: telegramUserId(),
        consent_package_version: CONSENT_VERSION,
        marketing_opt_in: input.marketingOptIn === true,
      }),
    });
    const json = await readJsonBody(res);
    if (!json) {
      return { ok: false, error: res.status === 405 ? "consent_api_unavailable" : "consent_write_failed" };
    }
    if (!res.ok || json.ok !== true) {
      return { ok: false, error: typeof json.error === "string" ? json.error : "consent_write_failed" };
    }
    const marketingRaw = json.marketing;
    let marketing:
      | { attempted: boolean; ok: boolean; granted: boolean; error?: string }
      | undefined;
    if (marketingRaw && typeof marketingRaw === "object") {
      const m = marketingRaw as Record<string, unknown>;
      marketing = {
        attempted: m.attempted === true,
        ok: m.ok !== false,
        granted: m.granted === true,
        ...(typeof m.error === "string" ? { error: m.error } : {}),
      };
    }
    return { ok: true, duplicate: json.duplicate === true, ...(marketing ? { marketing } : {}) };
  } catch {
    return { ok: false, error: "consent_network_failed" };
  }
}

export async function fetchLegalConsentStatus(sessionKey: string): Promise<{
  ok: boolean;
  status: "current" | "needs_update" | "none" | "unknown";
  onboarding_reset: boolean;
}> {
  try {
    const uid = telegramUserId();
    const qs = new URLSearchParams();
    if (sessionKey) qs.set("session_key", sessionKey);
    if (uid) qs.set("telegram_user_id", uid);
    await waitForTelegramInitData(1800);
    const res = await fetch(`/api/store-v2/legal-consent?${qs.toString()}`, {
      method: "GET",
      headers: storeV2AuthHeaders({ Accept: "application/json" }),
    });
    const json = await readJsonBody(res);
    if (!json || !res.ok || json.ok !== true) {
      return { ok: false, status: "unknown", onboarding_reset: false };
    }
    const status =
      json.status === "current" || json.status === "needs_update" || json.status === "none"
        ? json.status
        : "unknown";
    return { ok: true, status, onboarding_reset: json.onboarding_reset === true };
  } catch {
    return { ok: false, status: "unknown", onboarding_reset: false };
  }
}
